import { Container, Row, Col, Card } from 'react-bootstrap';
import { Target, Eye, Heart, Award, Shield, Lightbulb, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PageBanner } from '../components/layout/PageBanner';

const About = () => {
  const { t, language } = useLanguage();

  const values = [
    { 
      icon: Shield, 
      title: t('about.values.integrity'),
      description: language === 'hi' 
        ? 'हम सभी व्यापारिक व्यवहारों में पारदर्शिता और ईमानदारी बनाए रखते हैं।' 
        : 'We maintain transparency and honesty in all business dealings.'
    },
    { 
      icon: Lightbulb, 
      title: t('about.values.innovation'),
      description: language === 'hi' 
        ? 'नवीन समाधानों के माध्यम से निरंतर सुधार और उत्कृष्टता।' 
        : 'Continuous improvement and excellence through innovative solutions.'
    },
    { 
      icon: Users, 
      title: t('about.values.commitment'),
      description: language === 'hi' 
        ? 'हमारे ग्राहकों की सफलता के लिए समर्पित साझेदारी।' 
        : 'Dedicated partnership for our clients\' success.'
    },
    { 
      icon: Award, 
      title: t('about.values.quality'),
      description: language === 'hi' 
        ? 'हर परियोजना में उच्चतम गुणवत्ता मानक।' 
        : 'Highest quality standards in every project.'
    }
  ];

  return (
    <main id="main-content">
      <PageBanner 
        title={t('about.pageTitle')} 
        breadcrumb={t('about.breadcrumb')} 
      />

      {/* Organization Overview */}
      <section className="gov-section">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <h2 className="gov-section-heading" style={{ textAlign: 'left' }}>
                {t('about.overviewTitle')}
              </h2>
              <p style={{ 
                color: 'hsl(var(--muted-foreground))',
                fontSize: '1.0625rem',
                lineHeight: 1.7,
                marginBottom: '1.5rem'
              }}>
                {t('about.overviewText1')}
              </p>
              <p style={{ 
                color: 'hsl(var(--muted-foreground))',
                fontSize: '1.0625rem',
                lineHeight: 1.7
              }}>
                {t('about.overviewText2')}
              </p>

              {/* Company Details Card */}
              <div 
                style={{
                  background: 'hsl(var(--secondary))',
                  borderRadius: 'var(--radius-lg)',
                  padding: '1.5rem',
                  marginTop: '2rem',
                  border: '1px solid hsl(var(--border))'
                }}
              >
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>
                  {language === 'hi' ? 'कंपनी विवरण' : 'Company Details'}
                </h4>
                <div style={{ fontSize: '0.875rem' }}>
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>CIN:</strong> U62099UP2025PTC223917
                  </p>
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>{language === 'hi' ? 'वेबसाइट' : 'Website'}:</strong>{' '}
                    <a 
                      href="https://www.igttech.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: 'hsl(var(--primary))' }}
                    >
                      www.igttech.com
                    </a>
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    <strong>{language === 'hi' ? 'स्थिति' : 'Status'}:</strong>{' '}
                    <span style={{ color: 'hsl(var(--success))' }}>
                      {language === 'hi' ? 'सक्रिय' : 'Active'}
                    </span>
                  </p>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              {/* Visual Element */}
              <div 
                style={{
                  background: 'var(--gradient-gov-banner)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '3rem',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div 
                    style={{
                      width: '80px',
                      height: '80px',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1.5rem'
                    }}
                  >
                    <span style={{ fontSize: '2rem', fontWeight: '700' }}>IGT</span>
                  </div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Imagenators Global Technology
                  </h3>
                  <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '1.5rem' }}>
                    Private Limited
                  </p>
                  <div 
                    style={{
                      display: 'inline-block',
                      background: 'rgba(255,255,255,0.15)',
                      padding: '0.5rem 1rem',
                      borderRadius: '50px',
                      fontSize: '0.875rem'
                    }}
                  >
                    {language === 'hi' ? 'तकनीक • नवाचार • विकास' : 'Technology • Innovation • Growth'}
                  </div>
                </div>

                {/* Decorative Elements */}
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '-50px',
                    right: '-50px',
                    width: '200px',
                    height: '200px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    borderRadius: '50%'
                  }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission & Vision */}
      <section className="gov-section gov-section-alt">
        <Container>
          <Row className="g-4">
            <Col md={6}>
              <Card className="gov-card h-100">
                <Card.Body className="p-4">
                  <div 
                    style={{
                      width: '56px',
                      height: '56px',
                      background: 'hsl(var(--primary) / 0.1)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1.5rem'
                    }}
                  >
                    <Target size={28} style={{ color: 'hsl(var(--primary))' }} />
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                    {t('about.missionTitle')}
                  </h3>
                  <p style={{ 
                    color: 'hsl(var(--muted-foreground))',
                    fontSize: '1rem',
                    lineHeight: 1.7,
                    marginBottom: 0
                  }}>
                    {t('about.missionText')}
                  </p>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="gov-card h-100">
                <Card.Body className="p-4">
                  <div 
                    style={{
                      width: '56px',
                      height: '56px',
                      background: 'hsl(var(--accent) / 0.1)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1.5rem'
                    }}
                  >
                    <Eye size={28} style={{ color: 'hsl(var(--accent))' }} />
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                    {t('about.visionTitle')}
                  </h3>
                  <p style={{ 
                    color: 'hsl(var(--muted-foreground))',
                    fontSize: '1rem',
                    lineHeight: 1.7,
                    marginBottom: 0
                  }}>
                    {t('about.visionText')}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Core Values */}
      <section className="gov-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="gov-section-heading">
              {t('about.valuesTitle')}
            </h2>
          </div>

          <Row className="g-4">
            {values.map((value, index) => (
              <Col key={index} md={6} lg={3}>
                <div 
                  className="text-center p-4 h-100"
                  style={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 'var(--radius-lg)',
                    transition: 'all 0.25s ease'
                  }}
                >
                  <div 
                    style={{
                      width: '64px',
                      height: '64px',
                      background: 'var(--gradient-accent-subtle)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1.25rem'
                    }}
                  >
                    <value.icon size={28} style={{ color: 'hsl(var(--primary))' }} />
                  </div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                    {value.title}
                  </h4>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: 'hsl(var(--muted-foreground))',
                    marginBottom: 0
                  }}>
                    {value.description}
                  </p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Focus Areas */}
      <section className="gov-section gov-section-alt">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="text-center">
                <h2 className="gov-section-heading">
                  {language === 'hi' ? 'हमारे फोकस क्षेत्र' : 'Our Focus Areas'}
                </h2>
                <p style={{ 
                  color: 'hsl(var(--muted-foreground))',
                  marginBottom: '2.5rem',
                  fontSize: '1.0625rem'
                }}>
                  {language === 'hi' 
                    ? 'IGT निम्नलिखित क्षेत्रों में विशेष सेवाएं प्रदान करता है' 
                    : 'IGT provides specialized services in the following sectors'}
                </p>

                <Row className="g-4">
                  {[
                    { 
                      title: language === 'hi' ? 'सरकारी विभाग' : 'Government Departments',
                      description: language === 'hi' ? 'ई-गवर्नेंस और डिजिटल परिवर्तन' : 'E-governance and digital transformation'
                    },
                    { 
                      title: language === 'hi' ? 'शैक्षणिक संस्थान' : 'Educational Institutions',
                      description: language === 'hi' ? 'स्कूल, कॉलेज और विश्वविद्यालय' : 'Schools, colleges, and universities'
                    },
                    { 
                      title: language === 'hi' ? 'सार्वजनिक क्षेत्र' : 'Public Sector',
                      description: language === 'hi' ? 'PSUs और स्वायत्त निकाय' : 'PSUs and autonomous bodies'
                    },
                    { 
                      title: language === 'hi' ? 'कॉर्पोरेट उद्यम' : 'Corporate Enterprises',
                      description: language === 'hi' ? 'व्यावसायिक आईटी समाधान' : 'Business IT solutions'
                    }
                  ].map((area, index) => (
                    <Col key={index} sm={6} md={3}>
                      <div 
                        style={{
                          background: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: 'var(--radius)',
                          padding: '1.5rem 1rem',
                          height: '100%'
                        }}
                      >
                        <Heart 
                          size={24} 
                          style={{ 
                            color: 'hsl(var(--highlight))',
                            marginBottom: '0.75rem'
                          }} 
                        />
                        <h5 style={{ fontSize: '0.9375rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                          {area.title}
                        </h5>
                        <p style={{ 
                          fontSize: '0.8125rem', 
                          color: 'hsl(var(--muted-foreground))',
                          marginBottom: 0
                        }}>
                          {area.description}
                        </p>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default About;
